// The Messages console's LEFT PANE — the queue you work.
//
// Replaces the SMS-thread list. Three filters over one client list, one row shape
// in all of them: name · why it is here · stage · ONE next action · age.
//
// Design intent (minimal, and every rule here is load-bearing):
//  - No icon clusters. The next action is a VERB you can read at a glance.
//  - Age is red only when it costs money (clientQueue.isUrgent), so a red
//    badge keeps meaning something.
//  - Selection is a left accent bar + a tint, not a heavy card. The list is
//    scanned dozens of times a day; it should stay quiet.
//  - Empty states say what the view MEANS, never "no results".
//
// Presentational only. All data arrives as rows; MessagesPage owns fetching.

import React, { useRef, useState } from "react";
import { Badge, Box, Collapse, Stack, Tooltip, Typography, useMediaQuery } from "@mui/material";
import { TransitionGroup } from "react-transition-group";
import SearchIcon from "@mui/icons-material/Search";
import {
  QUEUE_VIEWS,
  type QueueGroup,
  type QueueRow,
  type QueueViewId,
} from "./clientQueue";
import type { QueueAgentOption, QueueScopeInfo } from "../../services/clientQueueService";

export interface QueueRailProps {
  rows: QueueRow[];
  view: QueueViewId;
  onViewChange: (v: QueueViewId) => void;
  selectedClientId: string | null;
  /** Row click. `action` is the row's declared next action (e.g. "Check in")
   * so the page can prepare it — the row is the button, the label is the verb. */
  onSelect: (clientId: string, action?: string) => void;
  onDismiss?: (clientId: string) => void;
  dismissalAvailable?: boolean;
  /** Per-view row counts for the switcher badges. */
  counts: Partial<Record<QueueViewId, number>>;
  loading?: boolean;
  search: string;
  onSearchChange: (v: string) => void;
  /** Ch78 · engaged (default) or everyone. */
  /** Ch78 · whose clients: "mine" · "all" · an agent id. */
  agent?: string;
  onAgentChange?: (agent: string) => void;
  /** The roster the server allowed this caller to switch across. Empty =
   * no switcher (an agent sees only their own list). */
  agents?: QueueAgentOption[];
  viewerId?: string;
  agentScope?: QueueScopeInfo;
  groupFilter?: QueueGroup | null;
  onClearGroup?: () => void;
}

const initials = (name: string): string =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("") || "?";

const EMPTY_COPY: Record<QueueViewId, string> = {
  needs_reply: "No one is waiting on a reply.",
  follow_up: "No follow-ups to work right now.",
  all: "No clients in this list yet.",
  needs_action: "Nothing needs you right now. That is the goal, not a bug.",
  inbox: "No one is waiting on a reply. Inbox zero.",
  pipeline: "No active clients in any stage yet.",
  recent: "No activity recorded yet.",
};

export default function QueueRail({
  rows,
  view,
  onViewChange,
  selectedClientId,
  onSelect,
  onDismiss,
  dismissalAvailable = false,
  counts,
  loading,
  search,
  onSearchChange,
  agent = "mine",
  onAgentChange,
  agents = [],
  viewerId,
  agentScope,
  groupFilter, onClearGroup,
}: QueueRailProps) {
  const dismissingKeys = useRef(new Set<string>());
  const reducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const canSwitchAgent = Boolean(onAgentChange) && (agentScope?.can_switch ?? agents.length > 0);
  const activeView = QUEUE_VIEWS.find((v) => v.id === view);
  // Ch78 · folded sections. Gone-cold starts folded: it is a list to work
  // through on a slow afternoon, not today's queue.
  const [folded, setFolded] = useState<Set<string>>(() => new Set(["cold"]));
  const toggleFold = (group: string) =>
    setFolded((prev) => {
      const next = new Set(prev);
      if (next.has(group)) next.delete(group);
      else next.add(group);
      return next;
    });
  const foldedGroups = new Set(rows.filter(r => r.kind === "header" && r.collapsible && r.group && folded.has(r.group)).map(r => r.group));
  const visibleRows = rows.filter((r) => r.kind === "header" || !r.group || !foldedGroups.has(r.group));

  return (
    <Box
      data-testid="queue-rail"
      sx={{
        // The PARENT (MessagesPage's list pane) owns the width — it is the only
        // node that knows the viewport. Pinning 316px here overrode every
        // responsive width the pane tried to hand down.
        width: "100%",
        minWidth: 0,
        flexShrink: 0,
        // Ch78 · `flex: 1` is what makes the list SCROLL. Without it this
        // column sized itself to its content, overflowed the pane, and the
        // pane clipped it — Grant: "the sidebar isn't scrollable".
        flex: 1,
        borderRight: (t) => `1px solid ${t.palette.divider}`,
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
      }}
    >
      <Box sx={{ p: 1.5, pb: 1, display: "flex", flexDirection: "column", gap: 1.25 }}>
        {/* Ch78 · WHOSE clients. Only managers/admins get the roster. */}
        {canSwitchAgent && (
          <Box
            component="select"
            value={agent}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => onAgentChange?.(e.target.value)}
            data-testid="queue-agent-switch"
            aria-label="Whose clients"
            sx={{
              height: 32,
              px: 1,
              borderRadius: 1.5,
              border: (t) => `1px solid ${t.palette.divider}`,
              bgcolor: "transparent",
              color: "text.primary",
              fontFamily: "inherit",
              fontSize: 12.5,
              fontWeight: 600,
            }}
          >
            <option value="mine">Me</option>
            {agents.filter((a) => a.id !== viewerId).map((a) => (
              <option key={a.id} value={a.id}>
                {a.full_name.split(/\s+/)[0]}
              </option>
            ))}
            <option value="all">Everyone</option>
          </Box>
        )}
        {/* Search — reads as a field, behaves as a filter over the loaded set. */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 1.25,
            height: 36,
            borderRadius: 1.5,
            border: (t) => `1px solid ${t.palette.divider}`,
          }}
        >
          <SearchIcon sx={{ fontSize: 16, color: "text.disabled" }} />
          <Box
            component="input"
            value={search}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              onSearchChange(e.target.value)
            }
            placeholder="Search clients, phone…"
            data-testid="queue-search"
            sx={{
              flex: 1,
              minWidth: 0,
              border: "none",
              outline: "none",
              background: "transparent",
              color: "text.primary",
              fontSize: 13,
              fontFamily: "inherit",
              "&::placeholder": { color: "text.disabled" },
            }}
          />
        </Box>

        {/* View switcher. Segmented, not tabs — these are sorts of one list. */}
        {/* 🔴 THE PILLS MUST NEVER BE NARROWER THAN THEIR OWN LABELS.
            v1 gave each pill `flex: 1` (= `flex: 1 1 0%`) plus `minWidth: 0`.
            `flex-basis: 0` makes the four pills share the rail equally and
            `minWidth: 0` deletes the automatic min-content floor that would
            otherwise stop the shrink. So the width each pill got had nothing
            to do with the text inside it: at a 316px rail every pill was 69px
            while "Needs action 114" needs 89px (measured in
            e2e/messages/messages-layout.spec.ts). The label carries
            `white-space: nowrap`, so it could not wrap, and the button is a
            `justify-content: center` flex box, so the 20px of overflow was
            split EVENLY ACROSS BOTH EDGES — 10px off the left, 10px off the
            right. That is the production symptom exactly: the leading "N" of
            "Needs action" sat outside the rail ("leeds action") while the
            trailing "114" badge ran into the next pill ("114Inbox").
            The counts were the trigger: three digits is what pushed content
            past the fixed share.

            The fix is to restore the floor (`minWidth: "max-content"`, so a
            pill can never be sized below the text it must show) and let the
            ROW wrap when the pills genuinely cannot share one line. Wrapping
            is what makes it degrade correctly at 3- and 4-digit counts and on
            a narrow rail: the row grows a second line instead of clipping.
            `flex: "1 1 auto"` still spreads leftover space evenly, so the
            common case looks like the segmented control it always was. */}
        <Stack
          direction="row"
          data-testid="queue-view-switcher"
          sx={{
            p: 0.375,
            gap: 0.5,
            rowGap: 0.5,
            flexWrap: "wrap",
            borderRadius: 1.5,
            bgcolor: "action.hover",
          }}
        >
          {QUEUE_VIEWS.map((v) => {
            const on = v.id === view;
            const n = counts[v.id] ?? 0;
            return (
              <Box
                key={v.id}
                component="button"
                onClick={() => onViewChange(v.id)}
                data-testid={`queue-view-${v.id}`}
                aria-pressed={on}
                sx={{
                  flex: "1 1 auto",
                  // Never below the width of the label + badge it must render.
                  minWidth: "max-content",
                  border: "none",
                  cursor: "pointer",
                  height: 28,
                  px: 1,
                  borderRadius: 1.25,
                  fontFamily: "inherit",
                  fontSize: 12,
                  fontWeight: on ? 700 : 500,
                  color: on ? "primary.main" : "text.secondary",
                  bgcolor: on ? "background.paper" : "transparent",
                  boxShadow: on ? 1 : "none",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 0.5,
                  transition: "background-color 120ms ease",
                  "&:hover": on ? {} : { color: "text.primary" },
                }}
              >
                <Box
                  component="span"
                  data-testid="queue-view-label"
                  sx={{ whiteSpace: "nowrap" }}
                >
                  {v.label}
                </Box>
                {v.id !== "all" && (
                  <Box
                    component="span"
                    sx={{
                      fontSize: 10.5,
                      fontWeight: 700,
                      px: 0.5,
                      borderRadius: 0.75,
                      bgcolor: on ? "primary.main" : "action.selected",
                      color: on ? "primary.contrastText" : "text.secondary",
                    }}
                  >
                    {n}
                  </Box>
                )}
              </Box>
            );
          })}
        </Stack>

        {groupFilter && <Box component="button" onClick={onClearGroup} sx={{ color: "primary.main", bgcolor: "transparent", border: 0, textAlign: "left", cursor: "pointer" }}>Showing {groupFilter === "money" ? "Money out" : groupFilter === "cold" ? "Gone cold" : "Follow up"} · Clear filter</Box>}
        {activeView && (
          <Typography variant="caption" sx={{ color: "text.disabled", lineHeight: 1.4 }}>
            {activeView.hint}
          </Typography>
        )}

        {/* PKT-0693 S2 · the Engaged/Everyone pill lived here. Deleted: a mode is
            state the operator carries in his head, and its default silently hid
            people on a fresh session. `isEngaged` now feeds PLACEMENT, in
            clientQueue.ts — nobody is hidden. */}
      </Box>

      <Box
        sx={{ flex: 1, minHeight: 0, overflowY: "auto", px: 1, pb: 1.5 }}
        data-testid="queue-rows"
      >
        {loading ? (
          <Stack spacing={0.5} sx={{ px: 0.5, pt: 1 }}>
            {[0, 1, 2, 3].map((i) => (
              <Box
                key={i}
                sx={{ height: 62, borderRadius: 1.5, bgcolor: "action.hover", opacity: 0.6 }}
              />
            ))}
          </Stack>
        ) : (
          <>
          {rows.length === 0 && <Typography
            variant="body2"
            data-testid="queue-empty"
            sx={{ color: "text.disabled", px: 1.5, py: 4, textAlign: "center", lineHeight: 1.6 }}
          >
            {EMPTY_COPY[view]}
          </Typography>}
          <TransitionGroup
            key={JSON.stringify([view, agent, search, groupFilter])}
            component={null}
            enter={false}
            childFactory={(child: React.ReactElement) => {
              const rowKey = child.props["data-row-key"] as string;
              // Retained children also need the current dismissal-only exit policy.
              if (child.props.in) dismissingKeys.current.delete(rowKey);
              return React.cloneElement(child, {
                exit: !reducedMotion && dismissingKeys.current.has(rowKey),
              });
            }}
          >
          {visibleRows.map((r) => (
            <Collapse key={r.key} data-row-key={r.key} timeout={reducedMotion ? 0 : 180} onExited={() => dismissingKeys.current.delete(r.key)}>
            {r.kind === "header" ? (
              <Stack
                key={r.key}
                direction="row"
                alignItems="center"
                spacing={1}
                component={r.collapsible ? "button" : "div"}
                onClick={r.collapsible && r.group ? () => toggleFold(r.group as string) : undefined}
                data-testid={r.group ? `queue-header-${r.group}` : undefined}
                aria-expanded={r.collapsible && r.group ? !folded.has(r.group) : undefined}
                sx={{
                  px: 1,
                  pt: 2,
                  pb: 0.75,
                  width: "100%",
                  border: "none",
                  background: "transparent",
                  textAlign: "left",
                  cursor: r.collapsible ? "pointer" : "default",
                  color: "text.primary",
                  fontFamily: "inherit",
                }}
              >
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    bgcolor: r.stage?.color || "text.disabled",
                  }}
                />
                <Typography
                  variant="overline"
                  sx={{ color: "text.secondary", lineHeight: 1, letterSpacing: 0.6 }}
                >
                  {r.title}
                </Typography>
                <Typography variant="caption" sx={{ color: "text.disabled" }}>
                  {r.count}
                </Typography>
                {r.collapsible && r.group && (
                  <Typography variant="caption" sx={{ color: "text.disabled", ml: "auto !important" }}>
                    {folded.has(r.group) ? "show · one check-in text each ▸" : "hide ▾"}
                  </Typography>
                )}
              </Stack>
            ) : (
              <Box
                key={r.key}
                component="div"
                onClick={() => r.clientId && onSelect(r.clientId, r.action)}
                role="button"
                tabIndex={0}
                onKeyDown={(event: React.KeyboardEvent) => { if (event.target === event.currentTarget && (event.key === "Enter" || event.key === " ")) { event.preventDefault(); r.clientId && onSelect(r.clientId, r.action); } }}
                data-testid={`queue-row-${r.clientId}`}
                aria-current={r.clientId === selectedClientId ? "true" : undefined}
                sx={{
                  display: "block",
                  width: "100%",
                  textAlign: "left",
                  border: "none",
                  cursor: "pointer",
                  fontFamily: "inherit",
                  // Ch78 · a native <button> does NOT inherit the page's text
                  // colour: the UA stylesheet paints it `buttontext` (black),
                  // so in dark mode every name in this rail rendered near-
                  // black on navy (Grant's 2026-09-07 screenshot). Set it.
                  color: "text.primary",
                  borderRadius: 1.5,
                  p: 1.25,
                  mb: 0.25,
                  position: "relative",
                  bgcolor:
                    r.clientId === selectedClientId ? "action.selected" : "transparent",
                  transition: "background-color 120ms ease",
                  "&:hover":
                    r.clientId === selectedClientId ? {} : { bgcolor: "action.hover" },
                  "&::before":
                    r.clientId === selectedClientId
                      ? {
                          content: '""',
                          position: "absolute",
                          left: 0,
                          top: 8,
                          bottom: 8,
                          width: 3,
                          borderRadius: 2,
                          bgcolor: "primary.main",
                        }
                      : undefined,
                }}
              >
                <Stack direction="row" spacing={1.25} alignItems="flex-start">
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      flexShrink: 0,
                      borderRadius: 1.25,
                      bgcolor: "action.selected",
                      color: "text.secondary",
                      fontSize: 11.5,
                      fontWeight: 700,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {initials(r.title)}
                  </Box>

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" alignItems="baseline" spacing={1}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          flex: 1,
                          minWidth: 0,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {r.title}
                      </Typography>
                      {r.test && (
                        // F24 · beside the name, in EVERY view: a row that is listed but not
                        // counted must say so wherever it appears.
                        <Box
                          component="span"
                          data-queue-chip="test"
                          sx={{ flexShrink: 0, fontSize: 10, fontWeight: 700, px: 0.75, py: 0.125, borderRadius: 0.75, color: "text.secondary", border: 1, borderColor: "divider", whiteSpace: "nowrap", alignSelf: "center" }}
                        >
                          TEST
                        </Box>
                      )}
                      <Typography
                        variant="caption"
                        sx={{
                          flexShrink: 0,
                          fontWeight: r.urgent ? 700 : 500,
                          color: r.urgent ? "error.main" : "text.disabled",
                        }}
                      >
                        {r.age}
                      </Typography>
                    </Stack>

                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        color: "text.secondary",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: r.action === "Review task" || r.task ? "normal" : "nowrap",
                      }}
                    >
                      {r.why}
                      {r.task && <Typography component="span" variant="caption" sx={{ display: "block", whiteSpace: "normal" }}>{r.task}</Typography>}
                      {r.action && <Typography component="span" variant="caption" sx={{ display: "block", color: t => t.palette.mode === "dark" ? t.palette.primary.light : t.palette.primary.main, fontWeight: 600, mt: 0.25 }}>{r.action}</Typography>}
                    </Typography>
                    {r.additionalObligations !== undefined && r.additionalObligations > 0 && (
                      <Typography
                        variant="caption"
                        data-testid={`queue-additional-obligations-${r.clientId}`}
                        sx={{ display: "block", color: "text.disabled", fontSize: "0.7rem", lineHeight: 1.2, mt: 0.25 }}
                      >
                        Also {r.additionalObligations} other {r.additionalObligations === 1 ? "obligation" : "obligations"}
                      </Typography>
                    )}

                    {/* 🔴 CHIPS WRAP; THEY DO NOT GET CRUSHED.
                        Flex items default to `flex-shrink: 1`, so when the
                        stage chip, the money chip and the action all compete for one
                        line the last one lost: "Upload Lease" was ellipsised
                        to "U.." in production, and the measured case in the
                        e2e spec squeezed "Reply" (33px of text) into 29px.
                        An ellipsis that leaves one letter is not a shortened
                        label, it is a missing one. Each chip now refuses to
                        shrink and the ROW wraps, so a chip that does not fit
                        beside its neighbours moves to the next line intact. */}
                    {view === "needs_action" && <Stack
                      direction="row"
                      spacing={0.75}
                      alignItems="center"
                      data-testid="queue-chip-row"
                      sx={{ mt: 0.5, flexWrap: "wrap", rowGap: 0.5 }}
                    >
                      {/* Ch78 · texting consent comes FIRST. "He checked YES to
                          SMS on the lead form — why isn't that the first thing
                          I see?" Green when they asked to be texted, red when
                          a STOP is on record, absent when nobody has asked. */}
                      {r.texts && (
                        <Box
                          component="span"
                          data-testid={`queue-texts-${r.clientId}`}
                          data-queue-chip="texts"
                          sx={{
                            flexShrink: 0,
                            maxWidth: "100%",
                            fontSize: 10,
                            fontWeight: 700,
                            px: 0.75,
                            py: 0.125,
                            borderRadius: 0.75,
                            color: r.texts.tone === "ok" ? "success.main" : "error.main",
                            bgcolor: (t) =>
                              r.texts?.tone === "ok"
                                ? `${t.palette.success.main}22`
                                : `${t.palette.error.main}22`,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {r.texts.label}
                        </Box>
                      )}
                      {r.stage && (
                        <Box
                          component="span"
                          data-queue-chip="stage"
                          sx={{
                            flexShrink: 0,
                            maxWidth: "100%",
                            fontSize: 10,
                            fontWeight: 700,
                            px: 0.75,
                            py: 0.125,
                            borderRadius: 0.75,
                            color: r.stage.color,
                            bgcolor: `${r.stage.color}22`,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {r.stage.label}
                        </Box>
                      )}
                      {/* Ch76 · the money chip. The wire has carried
                          open_invoice since client_queue.py shipped it; this
                          is where it finally lands. Red when overdue. */}
                      {r.invoice && (
                        <Box
                          component="span"
                          data-testid={`queue-invoice-${r.clientId}`}
                          data-queue-chip="invoice"
                          sx={{
                            flexShrink: 0,
                            maxWidth: "100%",
                            fontSize: 10,
                            fontWeight: 700,
                            px: 0.75,
                            py: 0.125,
                            borderRadius: 0.75,
                            color: r.invoiceOverdue ? "error.main" : "text.secondary",
                            bgcolor: (t) =>
                              r.invoiceOverdue
                                ? `${t.palette.error.main}22`
                                : t.palette.action.selected,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {r.invoice}
                        </Box>
                      )}
                      <Typography
                        variant="caption"
                        data-queue-chip="action"
                        sx={{
                          flexShrink: 0,
                          // Ellipsis is kept ONLY as the last resort for an
                          // action longer than the whole rail; it can no
                          // longer fire just because a neighbour took the room.
                          maxWidth: "100%",
                          fontWeight: 600,
                          color: t => t.palette.mode === "dark" ? t.palette.primary.light : t.palette.primary.main,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {r.action}
                      </Typography>
                    </Stack>}
                    {r.dismissible && dismissalAvailable && onDismiss && r.clientId && (
                      <Box component="button" onClick={(event: React.MouseEvent) => { event.stopPropagation(); dismissingKeys.current.add(r.key); onDismiss(r.clientId as string); }} data-testid={`queue-dismiss-${r.clientId}`} sx={{ mt: 0.5, p: 0, border: 0, bgcolor: "transparent", color: "text.disabled", cursor: "pointer", font: "inherit", fontSize: 11, textDecoration: "underline" }}>Dismiss</Box>
                    )}
                  </Box>
                </Stack>
              </Box>
            )}
            </Collapse>
          ))}
          </TransitionGroup>
          </>
        )}
      </Box>
    </Box>
  );
}
